/** Gym comments. Live mode uses public.gym_comments. Demo mode stays on this device. */
import type { CoverStore } from './gymCover';
import { sampleImageSource } from './sampleMedia';

const PREFIX = 'ggb.gym-comments.';
const MAX_COMMENTS = 200;

export type GymComment = {
  id: string;
  gymOwnerId: string;
  userId: string;
  displayName: string;
  username: string;
  avatarColor: string;
  avatarMediaId?: string;
  avatarUrl?: string;
  body: string;
  createdAt: string;
  parentId?: string;
};

const listeners = new Set<() => void>();
function publish() {
  for (const listener of listeners) listener();
}
export function subscribeGymComments(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function browserStore(): CoverStore {
  return localStorage;
}

export function gymCommentsKey(gymOwnerId: string) {
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(gymOwnerId))
    throw new Error('That comment could not be saved.');
  return PREFIX + gymOwnerId;
}

export function commentBody(value: string) {
  const clean = value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim();
  if (!clean) throw new Error('Write a comment first.');
  if (clean.length > 2000) throw new Error('Keep the comment under 2,000 characters.');
  return clean;
}

function text(value: unknown, max: number) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\u0000-\u001f]/g, ' ')
    .trim()
    .slice(0, max);
}

function row(value: unknown, gymOwnerId: string): GymComment | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Partial<GymComment>;
  if (typeof item.id !== 'string' || !/^[0-9a-f-]{36}$/i.test(item.id)) return null;
  if (item.gymOwnerId !== gymOwnerId || typeof item.userId !== 'string') return null;
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(item.userId) || item.userId === gymOwnerId) return null;
  let body = '';
  try {
    body = commentBody(typeof item.body === 'string' ? item.body : '');
  } catch {
    return null;
  }
  if (typeof item.createdAt !== 'string' || Number.isNaN(Date.parse(item.createdAt))) return null;
  const parentId =
    typeof item.parentId === 'string' &&
    /^[0-9a-f-]{36}$/i.test(item.parentId) &&
    item.parentId !== item.id
      ? item.parentId
      : undefined;
  return {
    id: item.id,
    gymOwnerId,
    userId: item.userId,
    displayName: text(item.displayName, 80) || 'Member',
    username: text(item.username, 30),
    avatarColor: /^#[0-9a-fA-F]{6}$/.test(item.avatarColor || '') ? item.avatarColor! : '#555555',
    avatarUrl: sampleImageSource(item.avatarUrl, item.userId) ?? undefined,
    avatarMediaId:
      typeof item.avatarMediaId === 'string' &&
      /^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(item.avatarMediaId)
        ? item.avatarMediaId
        : undefined,
    body,
    createdAt: item.createdAt,
    parentId,
  };
}

export function readGymComments(gymOwnerId: string, store: CoverStore = browserStore()) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(store.getItem(gymCommentsKey(gymOwnerId)) || '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((item) => row(item, gymOwnerId))
    .filter((item): item is GymComment => !!item)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function addGymComment(
  gymOwnerId: string,
  author: { id: string; displayName: string; username: string; avatarColor?: string },
  body: string,
  store: CoverStore = browserStore(),
  parentId?: string,
) {
  if (author.id === gymOwnerId) throw new Error('You can’t comment on your own gym.');
  const comments = readGymComments(gymOwnerId, store);
  let parent: string | undefined;
  if (parentId) {
    const target = comments.find((comment) => comment.id === parentId);
    if (!target) throw new Error('That comment is no longer here.');
    parent = target.parentId || target.id;
  }
  const next: GymComment = {
    id: crypto.randomUUID(),
    gymOwnerId,
    userId: author.id,
    displayName: text(author.displayName, 80) || 'Member',
    username: text(author.username, 30),
    avatarColor: /^#[0-9a-fA-F]{6}$/.test(author.avatarColor || '')
      ? author.avatarColor!
      : '#555555',
    body: commentBody(body),
    createdAt: new Date().toISOString(),
    parentId: parent,
  };
  if (comments.length >= MAX_COMMENTS) throw new Error('This gym has too many comments.');
  store.setItem(gymCommentsKey(gymOwnerId), JSON.stringify([next, ...comments]));
  publish();
  return next;
}

export function removeGymComment(
  gymOwnerId: string,
  commentId: string,
  actorId: string,
  store: CoverStore = browserStore(),
) {
  const comments = readGymComments(gymOwnerId, store);
  const target = comments.find((comment) => comment.id === commentId);
  if (!target) return;
  if (actorId !== gymOwnerId && actorId !== target.userId)
    throw new Error('You can’t remove that comment.');
  const next = comments.filter(
    (comment) => comment.id !== commentId && comment.parentId !== commentId,
  );
  if (next.length) store.setItem(gymCommentsKey(gymOwnerId), JSON.stringify(next));
  else store.removeItem(gymCommentsKey(gymOwnerId));
  publish();
}

export function clearGymComments(store: CoverStore = browserStore()) {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  for (const key of keys) store.removeItem(key);
  publish();
}
